import { useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import packageJson from '../../package.json';

const APP_VERSION = packageJson.version; // Importa automaticamente do package.json

/**
 * Hook otimizado para registro de eventos de usuário.
 * Registra apenas eventos essenciais para análise profissional.
 * 
 * @param {Object} params
 * @param {string} params.userId - ID do usuário autenticado
 * @param {string} params.tela - Nome da tela/rota
 * @param {string} params.evento - Tipo de evento (ex: 'page_view', 'click')
 * @param {Object} [params.detalhes] - Detalhes extras (opcional)
 * @param {boolean} [params.comDuração] - Se true, registra duração da permanência na tela
 */
export function useUserEvent({ userId, tela, evento = 'page_view', detalhes = {}, comDuração = false }) {
  const startTimeRef = useRef(null);
  const eventoIdRef = useRef(null);

  useEffect(() => {
    if (!userId || !tela) return;
    startTimeRef.current = Date.now();

    // Verifica se é primeira ação do dia (login diário)
    const registrarLoginDiario = async () => {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      
      const { data: acessosHoje } = await supabase
        .from('log_acessos')
        .select('id')
        .eq('user_id', userId)
        .gte('data_hora', hoje.toISOString())
        .limit(1);

      // Se não tiver nenhum acesso hoje, registra o login diário
      if (!acessosHoje || acessosHoje.length === 0) {
        const { data: usuario } = await supabase
          .from('usuarios')
          .select('email')
          .eq('id', userId)
          .single();

        await supabase.from('log_acessos').insert({
          user_id: userId,
          email: usuario?.email,
          data_hora: new Date().toISOString()
        });
      }
    };

    registrarLoginDiario();

    // Registra evento de entrada (apenas se for page_view)
    if (evento === 'page_view') {
      const eventoData = {
        user_id: userId,
        tela,
        evento,
        detalhes: { ...detalhes, app_version: APP_VERSION },
      };
      
      supabase.from('user_events').insert([eventoData]).select().then(({ data, error }) => {
        if (error) {
          console.error('Erro ao registrar evento:', error);
        } else if (data && data[0]) {
          eventoIdRef.current = data[0].id;
        }
      });
    }

    // Cleanup ao desmontar
    return () => {
      // Se for para registrar duração, atualiza o evento existente ou cria evento de saída
      if (comDuração && startTimeRef.current) {
        const duracao = Math.round((Date.now() - startTimeRef.current) / 1000);
        
        // Se temos o ID do evento, atualiza com a duração
        if (eventoIdRef.current) {
          supabase.from('user_events')
            .update({ duracao_segundos: duracao })
            .eq('id', eventoIdRef.current)
            .then(({ error }) => {
              if (error) console.error('Erro ao atualizar duração:', error);
            });
        } else {
          // Fallback: cria evento de saída (caso não tenha conseguido pegar o ID)
          supabase.from('user_events').insert([{
            user_id: userId,
            tela,
            evento: evento + '_leave',
            detalhes: { ...detalhes, app_version: APP_VERSION },
            duracao_segundos: duracao
          }]);
        }
      }
    };
    // eslint-disable-next-line
  }, [userId, tela]);
}
